import React from 'react'
import { Container, H1, H2, H3, H4 } from '../components/index'

export default class AccessGuide extends React.Component {
    render () {
        return (
            <div>
                <Container section>
                    <H1>How Tool Access Works</H1>
                    <div>                                                   
                        <br />We run 3 applications from vendor Atlassian
                        <ul className='ncss-list-ul'>
                            <li className='ncss-li'>Bitbucket</li>
                            <li className='ncss-li'>Confluence</li>
                            <li className='ncss-li'>Jira</li>
                        </ul>
                        <p>Access within each Atlassian app is based on permission levels. These levels work in a top-down hierarchy.</p>
                        <br />
                        <H4 className='pb5-sm'><strong>Primary Permission level</strong></H4>                       
                        At the topmost level, you must have <strong>application access</strong> permission, namely login rights to an application.<br />                        
                        <br />
                        <H4 className='pb5-sm'><strong>Secondary permission levels</strong></H4>
                        At subsequent levels, permissions are focused on specific <em>objects</em> within an application such as projects or spaces. 
                        Permissions at lower levels require that higher levels are in place. 
                        As an example, Confluence Page Restrictions won't take effect unless higher level App and Space permissions are in place.                        
                        <br className='dobule-space'/>The Tool Access Guide below describes objects within each app and their respective permission layers.
                    </div>                    
                </Container>
                <Container section>
                    <H1>Tool Access Guide</H1>
                    <br />
                    <div className='triple-container'>
                        <div className='split-container-left'>
                            <H3 className='pb5-sm'>JIRA Access</H3>                                                        
                            <H4 className='pb5-sm indent'><strong>Permission Levels</strong></H4>
                            <ol className='double-indent'>
                                <li><strong>Application access.</strong> Use this portal to check and gain access</li>
                                <li>Project permissions</li>
                                <li>(Optional) Issue Scurity</li>
                            </ol>                                                            
                            <br />
                            <H3 className='pb5-sm'>Project</H3>                            
                                <p>Project permissions are based on a permission "scheme" configured for the project at its creation time. The permission scheme consists of about 30 different rights including:</p>
                                <ul className='ncss-list-ul double-indent'>
                                    <li className='ncss-li'>View issue (called "Browse project")</li>
                                    <li className='ncss-li'>Edit issue</li>
                                    <li className='ncss-li'>Transition issue</li>
                                    <li className='ncss-li'>Manage sprints</li>
                                </ul>
                                <p>Each right is assigned to one or more fields or <em>roles</em>.</p>
                                <br />                            
                            <H4 className='pb5-sm'><strong>Roles</strong></H4>
                            <p>Project admins can assign users or groups to specific <em>roles</em>.
                            <br />There are 3 main roles:</p>
                            <ol className='double-indent'>
                                <li>Administrators - Manage the project</li>
                                <li>Developers - Can be assigned & work issues</li>
                                <li>Users - Read only access to issues</li>
                            </ol>
                            <p>Roles are an abstraction layer above users and groups. In one project you may be an Admin, in another a Developer, and a third a User.</p>
                            <br />
                            <H4 className='pb5-sm'><strong>Roles + Permission Scheme = Your rights within a project</strong></H4>
                            <p>The intersection of roles and the project permission scheme determines the rights you have within a project.
                            <br />
                            <em>Example:</em> In many project permission schemes, the <strong>Administrators</strong> role is assigned the "Manage Sprint" permission.</p>                            
                            <br />
                            <H3 className='pb5-sm'>Issue Security</H3>
                                <ul className='ncss-list-ul double-indent'>
                                    <li className='ncss-li'>Optional</li>
                                    <li className='ncss-li'>Is <strong>subtractive</strong> - cannot be used to grant access if a user does not have project permissions</li>
                                </ul>
                            <br />
                            <H4 className='pb5-sm'><strong>Reference Pages</strong></H4>
                                <ul className='ncss-list-ul double-indent'>
                                    <li className='ncss-li'>
                                        <a className='link' href='https://confluence.nike.com/display/BAAT/About+JIRA+Permissions' target='_blank'>About JIRA Permissions</a>
                                    </li>
                                    <li className='ncss-li'>
                                        <a className='link' href='https://confluence.nike.com/display/BAAT/How+to+add+users+to+project+roles+in+JIRA' target='_blank'>How to add users to project roles in JIRA</a>
                                    </li>
                                    <li className='ncss-li'>
                                        <a className='link' href='https://confluence.nike.com/display/BAAT/How+to+find+JIRA+project+admins' target='_blank'>How to find JIRA project admins</a>
                                    </li>
                                </ul>
                        </div>
                        <div className='split-container-center'>
                            <H3 className='pb5-sm'>Confluence Access</H3>
                            <H4 className='pb5-sm indent'><strong>Permission Levels</strong></H4>
                            <ol className='double-indent'>
                                <li><strong>Application access.</strong> Use this portal to check and gain access</li>
                                <li>Space permissions</li>
                                <li>(Optional) Page Restrictions</li>
                            </ol>
                            <br />
                            <H3 className='pb5-sm'>Space</H3>                            
                            <p>Space permissions are managed locally by space admins. There are about 15 different rights specified including:</p>
                            <ul className='ncss-list-ul double-indent'>
                                <li className='ncss-li'>View space</li>
                                <li className='ncss-li'>Add or edit pages</li>
                                <li className='ncss-li'>Add attachments and comments</li>
                            </ul>
                            <p>Space admins may add users and/or groups to space permissions.
                            <br />To list the space admins, navigate to the space. Use the <strong>gear</strong> icon in the lower left corner and select <strong>Overview</strong>.</p>
                            <br />
                            <H3 className='pb5-sm'>Page Restrictions</H3>
                            <ul className='ncss-list-ul double-indent'>
                                <li className='ncss-li'>Optional</li>
                                <li className='ncss-li'>Is <strong>subtractive</strong> - cannot be used to grant access if a user does not have space permissions</li>
                            </ul>                            
                            <br />
                            <H4 className='pb5-sm'><strong>Reference Pages</strong></H4>
                            <ul className='ncss-list-ul double-indent'>
                                <li className='ncss-li'>
                                    <a className='link' href='https://confluence.nike.com/display/BAAT/About+Confluence+Permissions' target='_blank'>About Confluence Permissions</a>
                                </li>
                                <li className='ncss-li'>
                                    <a className='link' href='https://confluence.nike.com/display/BAAT/About+Confluence+Page+Restrictions' target='_blank'>About Confluence Page Restrictions</a>
                                </li>
                                <li className='ncss-li'>
                                    <a className='link' href='https://confluence.nike.com/display/BAAT/How+a+Space+Administrator+can+manage+access+within+their+space' target='_blank'>How a Space Administrator can manage access within their space</a>
                                </li>
                                <li className='ncss-li'>
                                    <a className='link' href='https://confluence.nike.com/display/BAAT/How+to+find+Confluence+space+admins' target='_blank'>How to find Confluence space admins</a>
                                </li>
                            </ul>                                                           
                        </div>                            
                        <div className='split-container-right'>
                            <H3 className='pb5-sm'>Bitbucket Access</H3>
                            <H4 className='pb5-sm indent'><strong>Permission Levels</strong></H4>
                            <ol className='double-indent'>
                                <li><strong>Application access.</strong> Use this portal to check and gain access</li>
                                <li>Project permissions</li>
                                <li>Repo permissions</li>
                            </ol>
                            <br />
                            <H3 className='pb5-sm'>Project</H3>
                            <ul className='ncss-list-ul double-indent'>
                                <li className='ncss-li'>Project permissions are managed locally by project admins</li>
                                <li className='ncss-li'>To request a list of the Bitbucket Project admins, use the <strong>General Request</strong> form to submit a ticket</li>
                            </ul>
                            <br />
                            <H3 className='pb5-sm'>Repository</H3>
                            <ul className='ncss-list-ul double-indent'>
                                <li className='ncss-li'>Repository permissions allow you to extend access to users and groups beyond that already granted via project permissions.</li>
                                <li className='ncss-li'>Repository permissions are managed locally by repo admins</li>
                            </ul>
                            <br />
                            <H4 className='pb5-sm'><strong>Reference Pages</strong></H4>
                            <ul className='ncss-list-ul double-indent'>
                                <li className='ncss-li'>
                                    <a className='link' href='https://confluence.nike.com/display/BAAT/Create+and+manage+Bitbucket+Repositories' target='_blank'>Create and manage Bitbucket Repositories</a>
                                </li>
                            </ul>
                        </div>
                    </div>                    
                </Container>
            </div>
        )
    }
}


// WEBPACK FOOTER //
// ./client/src/accessSelfService/AccessGuide.js